import { ReactElement } from "react";
import { T, TF } from "react-translator-component";
import { Box, Grid, List, ListItem, ListItemIcon, SxProps, Typography } from "@mui/material";
import { FiberManualRecord } from "@mui/icons-material";
import { formatNumber } from "../../util/util";
import CopyText from "../CopyText";
import SafeTextWithLinks from "../SafeTextWithLinks";
import GroupIcon from "./GroupIcon";

const defaultImage = require("../../../assets/images/default.png");

interface Props {
  member_group: any;
  banner?: string;
  icon?: string;
}

export default function MemberGroupDetails({ member_group, banner, icon }: Props): ReactElement {
  const displayPrivateStatus = (_memberGroup: any) => {
    const type = _memberGroup.group_type.toLowerCase();
    switch(type) {
      case 'public': return T("mng_member_groups_private_setting_public")
      case 'private': return T("mng_member_groups_private_setting_private")
      default: return ""
    }
  }

  const activityList = (member_group_activities: any) => (
    <List dense sx={styles.activityList}>
      {member_group_activities.map((item: any, i: number) => (
        <ListItem key={i}>
          <ListItemIcon>
            <FiberManualRecord sx={styles.activityList.icon} />
          </ListItemIcon>
          {item.value}
        </ListItem>
      ))}
    </List>
  );

  const fee = (_memberGroup: any) => {
    const joining = _memberGroup.joining_fee_amount ? TF("mng_member_groups_fee_joining_value", formatNumber(_memberGroup.joining_fee_amount)) : T("mng_member_groups_fee_free");

    const type = _memberGroup.fee_type.toLowerCase();
    let _fee = T("mng_member_groups_fee_free");
    if (_memberGroup.fee_amount) {
      switch(type) {
        case "monthly": {
          _fee = TF("mng_member_groups_fee_monthly", formatNumber(_memberGroup.fee_amount));
          break;
        }
        case "yearly": {
          _fee = TF("mng_member_groups_fee_yearly", formatNumber(_memberGroup.fee_amount));
          break;
        }
      }
    }

    const item = (title: string, content: ReactElement | string) => (
      <>
        <Grid item component={"dt"} xs={2}>{title}</Grid>
        <Grid item component={"dd"} xs={10}>{content}</Grid>
      </>
    );
    return (
      <Grid container component={"dl"} sx={styles.fee}>
        {item(T("mng_member_groups_fee_joining"), joining)}
        {item(T("mng_member_groups_fee"), _fee)}
        {item(T("mng_member_groups_fee_explanation"), _memberGroup.fee_explanation ?? "")}
      </Grid>
    );
  }

  const gridItem = (title: string, content: ReactElement | string, dataCss?: SxProps) => (
    <>
      <Grid item component={"dt"} xs={2} sx={styles.title}>{title}</Grid>
      <Grid item component={"dd"} xs={10} sx={{ ...styles.data, ...dataCss }}>{content}</Grid>
    </>
  );

  if (!member_group) return (<></>);
  return (
    <Grid container component={"dl"} sx={styles.container}>
      {gridItem(
        T("mng_member_groups_name"),
        (
          <Box sx={styles.name}>
            <GroupIcon imgSrc={icon} />
            <CopyText text={member_group.name} />
          </Box>
        )
      )}
      {gridItem(
        T("mng_member_groups_banner_image"),
        <img src={banner ?? defaultImage} alt="" style={styles.banner} />
      )}
      {gridItem(T("mng_member_groups_private_setting"), displayPrivateStatus(member_group))}
      {gridItem(
        T("mng_member_groups_pii_needed"),
        member_group.personal_information_needed ? T("mng_member_groups_pii_needed_true") : T("mng_member_groups_pii_needed_false")
      )}
      {gridItem(
        T("mng_member_groups_description"),
        <Typography sx={styles.description}>{member_group.description}</Typography>
      )}
      {gridItem(
        T("mng_member_groups_activities"),
        activityList(member_group.member_group_activities),
        styles.activityListContainer
      )}
      {gridItem(
        T("mng_member_groups_social_links"),
        <SafeTextWithLinks text={member_group.social_links} />
      )}
      {gridItem(
        T("mng_member_groups_approval_required"),
        member_group.approval_required ? T("mng_member_groups_approval_required_true") : T("mng_member_groups_approval_required_false")
      )}
      {gridItem(
        T("mng_member_groups_fee"),
        fee(member_group),
        styles.feeContainer
      )}
      {gridItem(
        T("mng_member_groups_users_count"),
        formatNumber(member_group.member_group_users_count + 1)
      )}
      {gridItem(
        T("mng_member_groups_posts_count"),
        formatNumber(member_group.posts_count)
      )}
      {gridItem(
        T("mng_member_groups_digital_id"),
        <CopyText text={member_group.digital_id} />
      )}
    </Grid>
  );
};

const styles = {
  container : {
    width: "initial",
    maxHeight: "calc(100vh*0.9*0.7 - 90px)",
    overflowY: "auto" as const,
    border: "1px solid black",
    marginX: 2
  },
  title: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    borderRight: "1px solid black",
    borderBottom: "1px solid black",
    "&:last-of-type": {
      borderBottom: "none"
    },
  },
  data: {
    p: 1,
    borderBottom: "1px solid black",
    "&:last-of-type": {
      borderBottom: "none"
    },
  },
  name: {
    display: "flex",
    lineHeight: "200%",
    alignItems: "center"
  },
  banner: {
    /* Public screen と同じ */
    height: 220,
    width: "100%",
  },
  description: {
    whiteSpace: "pre-wrap"
  },
  activityListContainer: {
    padding: 0,
  },
  activityList: {
    "& .MuiListItem-root": {
      paddingLeft: 0
    },
    "& .MuiListItemIcon-root": {
      minWidth: 32,
      color: 'inherit',
      display: 'flex',
      justifyContent: 'center',
      marginTop: "0.5em",
      alignSelf: 'flex-start'
    },
    icon: {
      fontSize: '0.6rem'
    },
  },
  feeContainer: {
    padding: 0,
  },
  fee: {
    margin: 0,
    "& dt": {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      fontWeight: "normal",
      padding: 1,
      borderRight: "1px solid black",
      borderBottom: "1px solid black",
      "&:last-of-type": {
        borderBottom: "none"
      }
    },
    "& dd": {
      padding: 1,
      borderBottom: "1px solid black",
      whiteSpace: "pre-wrap",
      "&:last-of-type": {
        borderBottom: "none"
      }
    }
  }
}
